import '@mui/x-data-grid-premium/themeAugmentation'
import { Components, Theme } from '@mui/material'
import { omneaDataGridClasses } from '../../dataGridClasses'
import { menuContainerStyle, menuListStyle } from './MuiMenu'

export const MuiDataGrid: Components<Omit<Theme, 'components'>>['MuiDataGrid'] = {
  defaultProps: {
    slotProps: {
      // e.g. icon buttons in the headers for sort/filter
      baseIconButton: {
        variant: 'futureTheme.ghost',
      },
    },
  },
  styleOverrides: {
    root: ({ theme }) => ({
      '--DataGrid-overlayHeight': '300px',
      '--unstable_DataGrid-radius': theme.shape['futureTheme.radius-2xl'],
      '--DataGrid-rowBorderColor': theme.palette.futureTheme.border.strong,
      '--DataGrid-pinnedBackground': theme.palette.futureTheme.bg.surface.default,
      '--DataGrid-containerBackground': theme.palette.futureTheme.bg.surface.default,
      borderWidth: theme.futureTheme.strokeWidth.default,
      borderColor: theme.palette.futureTheme.border.strong,
      color: theme.palette.futureTheme.text.default,

      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,

      [`&.${omneaDataGridClasses.roundTop}`]: {
        borderTopLeftRadius: theme.shape['futureTheme.radius-2xl'],
        borderTopRightRadius: theme.shape['futureTheme.radius-2xl'],
      },
      // We no longer actually round the first child, we just round the whole thing
      [`&.${omneaDataGridClasses.roundFirstChildTop}`]: {
        borderTopLeftRadius: theme.shape['futureTheme.radius-2xl'],
        borderTopRightRadius: theme.shape['futureTheme.radius-2xl'],
      },

      '& .MuiDataGrid-withBorderColor': {
        borderColor: theme.palette.futureTheme.border.strong,
      },

      [`&.${omneaDataGridClasses.allowPageScroll}`]: {
        '& .MuiDataGrid-virtualScroller': {
          overflowY: 'visible !important',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        },
      },
      // Direct filler overrides don't work?
      '& .MuiDataGrid-filler > div': {
        borderTopWidth: theme.futureTheme.strokeWidth.default,
      },
    }),
    cell: ({ theme }) => ({
      padding: theme.spacing(3),
      ...theme.typography['futureTheme.body-regular'],
      color: theme.palette.futureTheme.text.default,
      borderTopWidth: theme.futureTheme.strokeWidth.default,
    }),
    columnHeader: ({ theme }) => ({
      padding: theme.spacing(3),
      ...theme.typography['futureTheme.body-medium'],
      color: theme.palette.futureTheme.text.muted,
    }),
    'row--borderBottom': ({ theme }) => ({
      '& .MuiDataGrid-columnHeader, & .MuiDataGrid-filler, & .MuiDataGrid-scrollbarFiller': {
        borderBottomWidth: theme.futureTheme.strokeWidth.default,
      },
    }),
    'container--top': ({ theme }) => ({
      '& [role="row"]': {
        backgroundColor: theme.palette.futureTheme.bg.surface.subtle,
      },
    }),
    'container--bottom': ({ theme }) => ({
      '& [role="row"]': {
        backgroundColor: theme.palette.futureTheme.bg.surface.subtle,
      },
    }),
    row: ({ theme }) => ({
      // DataGridBase uses a nested selector and !important, so we have to do a lot to override it here
      // We should remove most of that styling and move it here
      '&[role="row"]': {
        borderWidth: `${theme.futureTheme.strokeWidth.default} !important`,
        borderColor: theme.palette.futureTheme.border.strong,
      },
      '&:hover': {
        backgroundColor: `color-mix(in srgb, ${theme.palette.futureTheme.bg.inverted} 4%, transparent)`,
      },
    }),
    columnSeparator: {
      opacity: 0,
    },
    virtualScroller: ({ theme }) => ({
      backgroundColor: theme.palette.futureTheme.bg.surface.default,
    }),
    menu: ({ theme }) => ({
      '& .MuiPaper-root': menuContainerStyle(theme),
      '& .MuiDataGrid-menuList': menuListStyle(theme),
    }),
    panel: ({ theme }) => ({
      boxShadow: theme.futureTheme.shadows.sm,
    }),
    footerContainer: ({ theme }) => ({
      backgroundColor: theme.palette.futureTheme.bg.surface.default,
      borderTopWidth: theme.futureTheme.strokeWidth.default,
    }),
    pinnedColumns: {
      zIndex: 1,
    },
    'columnHeader--pinnedLeft': ({ theme }) => ({
      zIndex: 1,
      backgroundColor: theme.palette.futureTheme.bg.surface.default,
    }),
    'columnHeader--pinnedRight': ({ theme }) => ({
      zIndex: 1,
      backgroundColor: theme.palette.futureTheme.bg.surface.default,
    }),
    'cell--pinnedLeft': ({ theme }) => ({
      backgroundColor: theme.palette.futureTheme.bg.surface.default,
    }),
    'cell--pinnedRight': ({ theme }) => ({
      backgroundColor: theme.palette.futureTheme.bg.surface.default,
    }),
    'filler--pinnedLeft': ({ theme }) => ({
      backgroundColor: theme.palette.futureTheme.bg.surface.default,
    }),
    'filler--pinnedRight': ({ theme }) => ({
      backgroundColor: theme.palette.futureTheme.bg.surface.default,
    }),
    paper: ({ theme }) => ({
      borderWidth: theme.futureTheme.strokeWidth.default,
      borderStyle: 'solid',
      borderColor: theme.palette.futureTheme.border.subtle,
      borderRadius: theme.shape['futureTheme.radius-sm'],
      color: theme.palette.futureTheme.text.default,
      backgroundColor: theme.palette.futureTheme.bg.surface.elevated,
    }),
    columnHeaderTitleContainer: {
      // Sort icon in header shouldn't be clipped
      overflow: 'unset',
    },
    columnHeaderTitleContainerContent: {
      // Move sort icon to the right
      width: '100%',
    },
    aggregationColumnHeaderLabel: ({ theme }) => ({
      ...theme.typography['futureTheme.body-regular'],
    }),
    filterIcon: ({ theme }) => ({
      color: theme.palette.futureTheme.fg.muted,
    }),
    filterForm: ({ theme }) => ({
      gap: theme.spacing(1),
    }),
    panelFooter: ({ theme }) => ({
      marginTop: theme.spacing(4),
    }),
    columnsManagement: ({ theme }) => ({
      padding: theme.spacing(2),
      '& .MuiDataGrid-columnsManagementRow': {
        // Default is a negative margin
        marginLeft: 0,
        '& .MuiCheckbox-root': {
          // Aligned with default padding on Checkbox - DataGrid overrides it
          padding: theme.spacing(2),
        },
      },
    }),
    columnsManagementFooter: ({ theme }) => ({
      borderTopWidth: theme.futureTheme.strokeWidth.default,
      borderColor: theme.palette.futureTheme.border.subtle,
      padding: theme.spacing(2),
      '& .MuiFormControlLabel-root': {
        marginLeft: 0,

        '& .MuiCheckbox-root': {
          // Aligned with default padding on Checkbox - DataGrid overrides it
          padding: theme.spacing(2),
        },
      },
    }),
  },
}
